import { CarWheelPositionText, type InspectionItemRecord } from '@navio/nsdk';

export type InspectionCardTireDimensions = {
  all?: string;
  front?: string;
  rear?: string;
};

type DimensionLocation = NonNullable<InspectionItemRecord['location']>;

type DimensionItem = {
  location?: DimensionLocation | null;
  tire?: { specification?: string | null } | null;
};

const getConsistentDimension = (values: Array<string | undefined>) => {
  const definedValues = values.filter((value): value is string => Boolean(value));

  if (definedValues.length === 0) {
    return undefined;
  }

  return new Set(definedValues).size === 1 ? definedValues[0] : undefined;
};

/**
 * The dimensions a card states per axle, and once for the car when all four wheels agree.
 * An axle-level observation (`FRONT` / `REAR`) wins over the corners it covers.
 */
export const getInspectionCardTireDimensions = (items: DimensionItem[]): InspectionCardTireDimensions => {
  const byLocation: Partial<Record<DimensionLocation, string>> = {};
  for (const item of items) {
    const specification = item.tire?.specification;
    if (item.location && specification) {
      byLocation[item.location] = specification;
    }
  }

  const front =
    byLocation[CarWheelPositionText.FRONT] ??
    getConsistentDimension([byLocation[CarWheelPositionText.FRONT_LEFT], byLocation[CarWheelPositionText.FRONT_RIGHT]]);
  const rear =
    byLocation[CarWheelPositionText.REAR] ??
    getConsistentDimension([byLocation[CarWheelPositionText.REAR_LEFT], byLocation[CarWheelPositionText.REAR_RIGHT]]);

  // `all` only when every observation agrees -- one odd wheel has to stay visible per axle.
  return { all: getConsistentDimension(Object.values(byLocation)), front, rear };
};
